import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  MoreVert as MoreVertIcon,
  InsertEmoticon as EmojiIcon,
  Image as ImageIcon,
  Description as FileIcon,
  PictureAsPdf as PdfIcon,
  Archive as ZipIcon,
  Code as CodeIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Reply as ReplyIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as ListIcon,
  Link as LinkIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled-Komponenten für den Chat
const ChatContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3]
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50]
}));

const MessageBubble = styled(Box)(({ theme, isOwn }) => ({
  display: 'flex',
  flexDirection: isOwn ? 'row-reverse' : 'row',
  alignItems: 'flex-end',
  marginBottom: theme.spacing(3)
}));

const MessageContent = styled(Paper)(({ theme, isOwn }) => ({
  padding: theme.spacing(2),
  maxWidth: '85%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isOwn ? theme.palette.primary.light : theme.palette.background.paper,
  color: isOwn ? theme.palette.primary.contrastText : theme.palette.text.primary,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    [isOwn ? 'right' : 'left']: -8,
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: isOwn ? '0 0 8px 8px' : '0 8px 8px 0',
    borderColor: `transparent transparent ${isOwn ? theme.palette.primary.light : theme.palette.background.paper} transparent`
  }
}));

const ChatInputArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center'
}));

const ChatToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper
}));

// Funktion zum Generieren von Initialen aus einem Namen
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Funktion zum Generieren einer Farbe basierend auf dem Namen
const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', 
    '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', 
    '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'
  ];
  return colors[Math.abs(hash) % colors.length];
};

// Komponente für Avatar mit Initialen
const InitialsAvatar = ({ name, size = 36, src }) => {
  const initials = getInitials(name);
  const bgColor = getAvatarColor(name);
  
  if (src) {
    return <Avatar src={src} sx={{ width: size, height: size }} />;
  }
  
  return (
    <Avatar sx={{ 
      width: size, 
      height: size, 
      bgcolor: bgColor,
      fontSize: size * 0.4
    }}>
      {initials}
    </Avatar>
  );
};

// Hauptkomponente
const ChatInterface = ({ 
  chatId, 
  participants = [], 
  initialMessages = [],
  onSendMessage,
  onAttachFile,
  currentUser = { id: 'hospital', name: 'Universitätsklinikum', avatar: '/assets/hospital-avatar.png' }
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Tabs für verschiedene Chat-Kanäle
  const tabs = [
    { label: 'Gruppenchat', icon: <GroupIcon /> },
    { label: 'Pharma', icon: <PersonIcon /> },
    { label: 'ARGUS Support', icon: <BotIcon /> }
  ];

  // Scrollen zum Ende der Nachrichten
  useEffect(() => {
    scrollToBottom();
    console.log("Nachrichten aktualisiert:", messages);
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Nachricht senden
  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const newMsg = {
        id: Date.now(),
        sender: currentUser.id,
        senderName: currentUser.name,
        senderType: 'hospital',
        timestamp: new Date().toISOString(),
        content: newMessage,
        attachments: [],
        replyTo: replyTo,
        channel: tabs[activeTab].label.toLowerCase().replace(' ', '_')
      };
      
      setMessages([...messages, newMsg]);
      setNewMessage('');
      setReplyTo(null);
      
      if (onSendMessage) {
        onSendMessage(newMsg);
      }
    }
  };

  // Datei anhängen
  const handleAttachFile = (type) => {
    setAttachmentMenuAnchor(null);
    
    // Simuliere Dateiauswahl
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'image' ? 'image/*' : 
                  type === 'pdf' ? '.pdf' : 
                  type === 'code' ? '.js,.py,.java,.html,.css' : 
                  type === 'zip' ? '.zip,.rar,.7z' : 
                  '*/*';
    
    input.onchange = (e) => {
      if (e.target.files.length > 0) {
        const file = e.target.files[0];
        console.log(`Datei ausgewählt: ${file.name} (${file.type})`);
        
        // In einer echten Anwendung würde hier die Datei hochgeladen werden
        if (onAttachFile) {
          onAttachFile(file);
        }
        
        // Simuliere eine Nachricht mit Anhang
        const newMsg = {
          id: Date.now(),
          sender: currentUser.id,
          senderName: currentUser.name,
          senderType: 'hospital',
          timestamp: new Date().toISOString(),
          content: `Datei angehängt: ${file.name}`,
          attachments: [{
            id: Date.now(),
            name: file.name,
            type: file.type,
            size: `${(file.size / 1024).toFixed(2)} KB`,
            url: URL.createObjectURL(file)
          }],
          channel: tabs[activeTab].label.toLowerCase().replace(' ', '_')
        };
        
        setMessages([...messages, newMsg]);
      }
    };
    
    input.click();
  };

  // Formatierung für Nachrichten
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Prüfen, ob das Datum heute ist
    if (date.toDateString() === today.toDateString()) {
      return `Heute, ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Prüfen, ob das Datum gestern ist
    if (date.toDateString() === yesterday.toDateString()) {
      return `Gestern, ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Ansonsten vollständiges Datum zurückgeben
    return date.toLocaleString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Nachricht formatieren (z.B. Links, fett, kursiv)
  const formatMessageContent = (content) => {
    // Links erkennen und formatieren
    const linkRegex = /(https?:\/\/[^\s]+)/g;
    const formattedContent = content.replace(linkRegex, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Fett formatieren
    const boldRegex = /\*\*(.*?)\*\*/g;
    const withBold = formattedContent.replace(boldRegex, '<strong>$1</strong>');
    
    // Kursiv formatieren
    const italicRegex = /\*(.*?)\*/g;
    const withItalic = withBold.replace(italicRegex, '<em>$1</em>');
    
    // Code formatieren
    const codeRegex = /`(.*?)`/g;
    const withCode = withItalic.replace(codeRegex, '<code style="background-color: rgba(0,0,0,0.05); padding: 2px 4px; border-radius: 3px;">$1</code>');
    
    return <div dangerouslySetInnerHTML={{ __html: withCode }} style={{ lineHeight: '1.5' }} />;
  };

  // Anhang-Icon basierend auf Dateityp
  const getAttachmentIcon = (type) => {
    if (type.startsWith('image/')) return <ImageIcon />;
    if (type.includes('pdf')) return <PdfIcon />;
    if (type.includes('zip') || type.includes('rar') || type.includes('7z')) return <ZipIcon />;
    if (type.includes('javascript') || type.includes('python') || type.includes('java')) return <CodeIcon />;
    return <FileIcon />;
  };

  // Initialisiere Nachrichten aus den Props
  useEffect(() => {
    if (initialMessages && initialMessages.length > 0) {
      console.log("Initialisiere Nachrichten:", initialMessages);
      setMessages(initialMessages);
    }
  }, [initialMessages]);

  // Filtere Nachrichten basierend auf aktivem Tab
  const filteredMessages = messages.filter(msg => {
    if (activeTab === 0) return true; // Alle Nachrichten im Gruppenchat
    if (activeTab === 1) return msg.channel === 'pharma' || !msg.channel; // Pharma-Chat
    if (activeTab === 2) return msg.channel === 'argus_support'; // ARGUS-Support
    return true;
  });

  return (
    <ChatContainer>
      <ChatHeader>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InitialsAvatar 
            name={activeTab === 0 ? 'Gruppenchat' : 
                 activeTab === 1 ? 'Pharma' : 
                 'ARGUS Support'}
            src={activeTab === 0 ? null : 
                 activeTab === 1 ? '/assets/pharma-avatar.png' : 
                 '/assets/argus-avatar.png'} 
            size={40}
            sx={{ mr: 2 }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {tabs[activeTab].label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activeTab === 0 ? `${participants.length} Teilnehmer` : 'Direkter Chat'}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Tooltip title="Suchen">
            <IconButton>
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filtern">
            <IconButton>
              <FilterIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mehr Optionen">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => { setAnchorEl(null); }}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Chat exportieren</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Chat löschen</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </ChatHeader>
      
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((tab, index) => (
          <Tab 
            key={index} 
            icon={tab.icon} 
            label={tab.label} 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
        ))}
      </Tabs>
      
      <ChatMessages>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredMessages.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Keine Nachrichten vorhanden
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Starten Sie die Konversation mit einer Nachricht
            </Typography>
          </Box>
        ) : (
          filteredMessages.map((message) => {
            const isOwn = message.sender === currentUser.id;
            const replyToMessage = message.replyTo ? messages.find(m => m.id === message.replyTo) : null;
            
            return (
              <MessageBubble key={message.id} isOwn={isOwn}>
                {!isOwn && (
                  <InitialsAvatar 
                    name={message.senderName || message.sender}
                    src={null} 
                    sx={{ mr: 1 }}
                    size={40}
                  />
                )}
                <Box sx={{ maxWidth: '80%' }}>
                  {replyToMessage && (
                    <Box 
                      sx={{ 
                        p: 1, 
                        mb: 0.5, 
                        borderLeft: '3px solid', 
                        borderColor: 'primary.main',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        fontSize: '0.85rem',
                        color: 'text.secondary',
                        maxHeight: '60px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        {replyToMessage.senderName || replyToMessage.sender}:
                      </Typography>
                      <Typography variant="body2" noWrap>
                        {replyToMessage.content}
                      </Typography>
                    </Box>
                  )}
                  <MessageContent isOwn={isOwn}>
                    <Typography variant="subtitle2" color={isOwn ? "primary.contrastText" : "text.secondary"} sx={{ opacity: 0.9 }}>
                      {message.senderName || message.sender} • {formatTimestamp(message.timestamp)}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, wordBreak: 'break-word' }}>
                      {formatMessageContent(message.content)}
                    </Typography>
                    {message.attachments && message.attachments.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {message.attachments.map((attachment) => (
                          <Box 
                            key={attachment.id} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 1, 
                              bgcolor: isOwn ? 'primary.dark' : 'background.default',
                              borderRadius: 1,
                              '&:hover': {
                                bgcolor: isOwn ? 'primary.dark' : 'action.hover'
                              }
                            }}
                          >
                            {getAttachmentIcon(attachment.type)}
                            <Box sx={{ ml: 1, flexGrow: 1, overflow: 'hidden' }}>
                              <Typography variant="body2" noWrap>
                                {attachment.name}
                              </Typography>
                              <Typography variant="caption" color={isOwn ? "primary.contrastText" : "text.secondary"}>
                                {attachment.size}
                              </Typography>
                            </Box>
                            <Tooltip title="Herunterladen">
                              <IconButton size="small" sx={{ color: isOwn ? 'primary.contrastText' : 'primary.main' }}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </MessageContent>
                  <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', mt: 0.5 }}>
                    <Tooltip title="Antworten">
                      <IconButton 
                        size="small" 
                        onClick={() => setReplyTo(message.id)}
                        sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                      >
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {isOwn && (
                  <InitialsAvatar 
                    name={currentUser.name}
                    src={null} 
                    sx={{ ml: 1 }}
                    size={40}
                  />
                )}
              </MessageBubble>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </ChatMessages>
      
      {replyTo && (
        <Box 
          sx={{ 
            p: 1, 
            mx: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.default',
            borderTop: 1,
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReplyIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Antwort auf: {messages.find(m => m.id === replyTo)?.content.substring(0, 50)}
              {messages.find(m => m.id === replyTo)?.content.length > 50 ? '...' : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyTo(null)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      
      <ChatToolbar>
        <Tooltip title="Fett">
          <IconButton size="small">
            <BoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Kursiv">
          <IconButton size="small">
            <ItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Liste">
          <IconButton size="small">
            <ListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Link">
          <IconButton size="small">
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ChatToolbar>
      
      <ChatInputArea>
        <IconButton onClick={(e) => setAttachmentMenuAnchor(e.currentTarget)}>
          <AttachFileIcon />
        </IconButton>
        <Menu
          anchorEl={attachmentMenuAnchor}
          open={Boolean(attachmentMenuAnchor)}
          onClose={() => setAttachmentMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleAttachFile('image')}>
            <ListItemIcon>
              <ImageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bild</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleAttachFile('pdf')}>
            <ListItemIcon>
              <PdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF-Dokument</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleAttachFile('code')}>
            <ListItemIcon>
              <CodeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Code-Datei</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleAttachFile('zip')}>
            <ListItemIcon>
              <ZipIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Archiv (.zip, .rar)</ListItemText>
          </MenuItem>
        </Menu>
        <IconButton>
          <EmojiIcon />
        </IconButton>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Nachricht eingeben..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          multiline
          maxRows={4}
          size="small"
          sx={{ mx: 1 }}
        />
        <Button
          variant="contained"
          color="primary"
          endIcon={<SendIcon />}
          onClick={handleSendMessage}
          disabled={!newMessage.trim()}
        >
          Senden
        </Button>
      </ChatInputArea>
    </ChatContainer>
  );
};

export default ChatInterface;
